import { AccountPastDue } from "src/model/Financial";
import { AccountPastDueRequest, InvoiceRequest, SettlementEditRequest, SettlementRequest } from "src/requests/FinancialRequests";

import { Business, CreditApplicationState, Customer, CustomerTypeWithBoth, SimpleCustomer } from "../model/Customer";
import { ListWrapper } from "../model/ListWrapper";
import { AddBusinessContactRequest } from "../requests/AddBusinessContactRequest";
import {
    AddContactToBusinessRequest,
    AgentAuthorizationRequest,
    CreditApplicationRequest,
    CustomerRequest,
    InsuranceRecordRequest,
    RevokeAgentAuthorizationRequest
} from "../requests/CustomerRequests";
import { MultiPatchRequest } from "../requests/PatchRequests";
import { BuildUri } from "./util/BuildUri";
import { Fetch } from "./util/InterceptedFetch";

export interface CustomerFilterParameters {
    firstName?: string;
    lastName?: string;
    businessName?: string;
    type?: CustomerTypeWithBoth;
    fullTextSearch?: string;
    /**
     * Comma separated list of sale IDs that the Customer must have involvement with
     */
    involvementSaleIds?: string;
    /**
     * Comma separated list of involvement role names
     */
    involvementRoleIds?: string;
    includeArchived?: boolean;
}

export class CustomerEndpoints {
    constructor(private fetch: Fetch) {}

    getCustomerList(
        limit: number = 10,
        startAt: number = 0,
        orderBy: string | undefined = undefined,
        filters: CustomerFilterParameters = {}
    ): Promise<ListWrapper<SimpleCustomer>> {
        return this.fetch(BuildUri("/customer", {}, { limit, startAt, orderBy, ...filters }));
    }

    getCustomer(customerId: number): Promise<Customer> {
        return this.fetch(BuildUri("/customer/{customerId}", { customerId }));
    }

    createCustomer(request: CustomerRequest): Promise<Customer> {
        return this.fetch(BuildUri("/customer"), { method: "POST", body: request });
    }

    editCustomer(customerId: number, patchRequests: MultiPatchRequest): Promise<Customer> {
        return this.fetch(BuildUri("/customer/{customerId}", { customerId }), { method: "PATCH", body: patchRequests });
    }

    deleteCustomer(customerId: number): Promise<void> {
        return this.fetch(BuildUri("/customer/{customerId}", { customerId }), { method: "DELETE" });
    }

    addBusinessContact(businessId: number, request: AddBusinessContactRequest): Promise<Business> {
        return this.fetch(BuildUri("/customer/{businessId}", { businessId }), { body: request, method: "POST" });
    }

    addContactToBusiness(businessId: number, request: AddContactToBusinessRequest): Promise<Customer> {
        return this.fetch(BuildUri("/customer/{businessId}/contact", { businessId }), { body: request, method: "POST" });
    }

    removeContactFromBusiness(businessId: number, contactId: number): Promise<Customer> {
        return this.fetch(BuildUri("/customer/{businessId}/contact/{contactId}", { businessId, contactId }), { method: "DELETE" });
    }

    createCreditApplication(customerId: number, request: CreditApplicationRequest): Promise<Customer> {
        return this.fetch(BuildUri("/customer/{customerId}/credit-application", { customerId }), { method: "POST", body: request });
    }

    editCreditApplication(applicationId: number, request: CreditApplicationRequest): Promise<Customer> {
        return this.fetch(BuildUri("/customer/credit-application/{applicationId}", { applicationId }), { method: "PATCH", body: request });
    }

    creditApplicationStateChange(applicationId: number, applicationState: CreditApplicationState): Promise<Customer> {
        return this.fetch(BuildUri("/customer/credit-application/{applicationId}", { applicationId }), {
            method: "POST",
            body: { state: applicationState }
        });
    }

    createAgentAuthorization(request: AgentAuthorizationRequest): Promise<Customer> {
        return this.fetch(BuildUri("/customer/agent-authorization"), { method: "POST", body: request });
    }

    editAgentAuthorization(authorizationId: number, request: AgentAuthorizationRequest): Promise<Customer> {
        return this.fetch(BuildUri("/customer/agent-authorization/{authorizationId}", { authorizationId }), { method: "PATCH", body: request });
    }

    revokeAgentAuthorization(authorizationId: number, request: RevokeAgentAuthorizationRequest): Promise<Customer> {
        return this.fetch(BuildUri("/customer/agent-authorization/{authorizationId}/revoke", { authorizationId }), { method: "POST", body: request });
    }

    createInsuranceRecord(customerId: number, request: InsuranceRecordRequest): Promise<Customer> {
        return this.fetch(BuildUri("/customer/{customerId}/insurance-record", { customerId }), { method: "POST", body: request });
    }

    editInsuranceRecord(insuranceId: number, request: InsuranceRecordRequest): Promise<Customer> {
        return this.fetch(BuildUri("/customer/insurance-record/{insuranceId} ", { insuranceId }), { method: "PATCH", body: request });
    }

    cancelInsuranceRecord(insuranceId: number): Promise<Customer> {
        return this.fetch(BuildUri("/customer/insurance-record/{insuranceId} ", { insuranceId }), { method: "DELETE" });
    }

    // NOTE: Technically, the endpoints below belong in a separate FinanceEndpoint. These should match the back-end
    // so that it's easier to compare between the two code bases.

    createSettlement(accountId: number, request: SettlementRequest): Promise<Customer> {
        return this.fetch(BuildUri("/finance/account/{accountId}/settlement/", { accountId }), { method: "POST", body: request });
    }

    editSettlementDetails(settlememtId: number, request: SettlementEditRequest): Promise<Customer> {
        return this.fetch(BuildUri("/finance/settlement/{settlememtId}", { settlememtId }), { method: "PATCH", body: request });
    }

    deleteSettlement(settlememtId: number): Promise<Customer> {
        return this.fetch(BuildUri("/finance/settlement/{settlememtId}", { settlememtId }), { method: "DELETE" });
    }

    createInvoice(customerId: number, request: InvoiceRequest): Promise<Customer> {
        return this.fetch(BuildUri("/finance/account/{customerId}/invoice", { customerId }), { method: "POST", body: request });
    }

    editInvoice(invoiceId: number, request: InvoiceRequest): Promise<Customer> {
        return this.fetch(BuildUri("/finance/invoice/{invoiceId}", { invoiceId }), { method: "PUT", body: request });
    }

    voidInvoice(invoiceId: number): Promise<Customer> {
        return this.fetch(BuildUri("/finance/invoice/{invoiceId}", { invoiceId }), { method: "DELETE" });
    }

    downloadInvoice(invoiceIds: Array<number>): Promise<Blob> {
        return this.fetch(BuildUri("/finance/invoice/document", {}, { invoiceIds: invoiceIds.join(",") }), { returnBlob: true });
    }

    printStatement(customerId: number, to: string, from: string): Promise<Blob> {
        return this.fetch(BuildUri("/finance/account/{customerId}/statement?from={to}&to={from}", { customerId, to, from }), { returnBlob: true });
    }

    getAccountsPastDue(): Promise<Array<AccountPastDue>> {
        return this.fetch(BuildUri("/finance/account/outstanding"), {});
    }

    generateInterestInvoices(request: Array<AccountPastDueRequest>): Promise<void> {
        return this.fetch(BuildUri("/finance/account/invoice-batch"), { method: "POST", body: request });
    }
}



// WEBPACK FOOTER //
// ./src/endpoints/CustomerEndpoints.ts